import supabase from '../lib/supabase';
import { format, startOfMonth, endOfMonth, startOfQuarter, endOfQuarter, startOfYear, endOfYear } from 'date-fns';

// Indian states with their GST state codes
const indianStates = [
  { code: '01', name: 'Jammu and Kashmir', type: 'state' },
  { code: '02', name: 'Himachal Pradesh', type: 'state' },
  { code: '03', name: 'Punjab', type: 'state' },
  { code: '04', name: 'Chandigarh', type: 'ut' },
  { code: '05', name: 'Uttarakhand', type: 'state' },
  { code: '06', name: 'Haryana', type: 'state' },
  { code: '07', name: 'Delhi', type: 'ut' },
  { code: '08', name: 'Rajasthan', type: 'state' },
  { code: '09', name: 'Uttar Pradesh', type: 'state' },
  { code: '10', name: 'Bihar', type: 'state' },
  { code: '11', name: 'Sikkim', type: 'state' },
  { code: '12', name: 'Arunachal Pradesh', type: 'state' },
  { code: '13', name: 'Nagaland', type: 'state' },
  { code: '14', name: 'Manipur', type: 'state' },
  { code: '15', name: 'Mizoram', type: 'state' },
  { code: '16', name: 'Tripura', type: 'state' },
  { code: '17', name: 'Meghalaya', type: 'state' },
  { code: '18', name: 'Assam', type: 'state' },
  { code: '19', name: 'West Bengal', type: 'state' },
  { code: '20', name: 'Jharkhand', type: 'state' },
  { code: '21', name: 'Odisha', type: 'state' },
  { code: '22', name: 'Chhattisgarh', type: 'state' },
  { code: '23', name: 'Madhya Pradesh', type: 'state' },
  { code: '24', name: 'Gujarat', type: 'state' },
  { code: '25', name: 'Daman and Diu', type: 'ut' },
  { code: '26', name: 'Dadra and Nagar Haveli', type: 'ut' },
  { code: '27', name: 'Maharashtra', type: 'state' },
  { code: '28', name: 'Andhra Pradesh', type: 'state' },
  { code: '29', name: 'Karnataka', type: 'state' },
  { code: '30', name: 'Goa', type: 'state' },
  { code: '31', name: 'Lakshadweep', type: 'ut' },
  { code: '32', name: 'Kerala', type: 'state' },
  { code: '33', name: 'Tamil Nadu', type: 'state' },
  { code: '34', name: 'Puducherry', type: 'ut' },
  { code: '35', name: 'Andaman and Nicobar Islands', type: 'ut' },
  { code: '36', name: 'Telangana', type: 'state' },
  { code: '37', name: 'Andhra Pradesh (New)', type: 'state' },
  { code: '38', name: 'Ladakh', type: 'ut' }
];

// Demo company information
const companyInfo = {
  name: 'Digital Marketing Agency Pvt Ltd',
  gstin: '27AABCD1234E1Z5',
  state_code: '27', // Maharashtra
  state_name: 'Maharashtra',
  address: {
    line1: '123 Business Complex',
    line2: 'Andheri East',
    city: 'Mumbai',
    state: 'Maharashtra',
    pincode: '400069',
    country: 'India'
  },
  pan: 'AABCD1234E',
  email: 'gst@digitalagency.com',
  phone: '+91 9876543210'
};

class TaxService {
  constructor() {
    this.isOnline = false;
    this.checkConnection();
  }

  async checkConnection() {
    try {
      const { data, error } = await supabase.from('invoices_2024').select('count', { count: 'exact' });
      this.isOnline = !error;
    } catch (error) {
      this.isOnline = false;
    }
  }

  getStateByCode(stateCode) {
    return indianStates.find(state => state.code === stateCode);
  }

  getStateByName(stateName) {
    return indianStates.find(state => 
      state.name.toLowerCase() === stateName.toLowerCase()
    );
  }

  extractStateFromGSTIN(gstin) {
    if (!gstin || gstin.length < 2) return null;
    const stateCode = gstin.substring(0, 2);
    return this.getStateByCode(stateCode);
  }

  calculateGST(amount, gstRate, supplierState, recipientState, isB2B = true) {
    if (!isB2B || gstRate === 0) {
      return {
        cgst: 0,
        sgst: 0,
        igst: 0,
        total_tax: 0,
        tax_type: 'B2C'
      };
    }

    const taxAmount = (amount * gstRate) / 100;
    
    // Check if inter-state or intra-state
    const isInterState = supplierState !== recipientState;
    
    if (isInterState) {
      return {
        cgst: 0,
        sgst: 0,
        igst: taxAmount,
        total_tax: taxAmount,
        tax_type: 'IGST'
      };
    } else {
      return {
        cgst: taxAmount / 2,
        sgst: taxAmount / 2,
        igst: 0,
        total_tax: taxAmount,
        tax_type: 'CGST+SGST'
      };
    }
  }

  async getTaxSummary(startDate, endDate) {
    // Get all invoices and payments in the date range
    const { data: invoices } = await this.getInvoicesForPeriod(startDate, endDate);
    
    let summary = {
      period: {
        start_date: startDate,
        end_date: endDate
      },
      totals: {
        total_sales: 0,
        total_cgst: 0,
        total_sgst: 0,
        total_igst: 0,
        total_tax: 0,
        b2b_sales: 0,
        b2c_sales: 0,
        interstate_sales: 0,
        intrastate_sales: 0
      },
      by_state: {},
      by_rate: {},
      invoices: invoices || []
    };

    if (!invoices || invoices.length === 0) {
      return summary;
    }

    invoices.forEach(invoice => {
      const isB2B = invoice.client?.gst_type === 'B2B';
      const clientState = this.extractStateFromGSTIN(invoice.client?.gstin);
      const supplierState = this.getStateByCode(companyInfo.state_code);
      
      // Calculate tax breakdown
      const services = invoice.services || [];
      services.forEach(service => {
        const taxCalc = this.calculateGST(
          service.amount,
          service.tax_rate || 0,
          supplierState?.code,
          clientState?.code,
          isB2B
        );

        // Add to totals
        summary.totals.total_sales += service.amount;
        summary.totals.total_cgst += taxCalc.cgst;
        summary.totals.total_sgst += taxCalc.sgst;
        summary.totals.total_igst += taxCalc.igst;
        summary.totals.total_tax += taxCalc.total_tax;

        if (isB2B) {
          summary.totals.b2b_sales += service.amount;
        } else {
          summary.totals.b2c_sales += service.amount;
        }

        if (taxCalc.tax_type === 'IGST') {
          summary.totals.interstate_sales += service.amount;
        } else {
          summary.totals.intrastate_sales += service.amount;
        }

        // Group by state
        const stateKey = clientState?.name || 'Unknown';
        if (!summary.by_state[stateKey]) {
          summary.by_state[stateKey] = {
            state_name: stateKey,
            state_code: clientState?.code || 'XX',
            total_sales: 0,
            cgst: 0,
            sgst: 0,
            igst: 0,
            total_tax: 0
          };
        }
        
        summary.by_state[stateKey].total_sales += service.amount;
        summary.by_state[stateKey].cgst += taxCalc.cgst;
        summary.by_state[stateKey].sgst += taxCalc.sgst;
        summary.by_state[stateKey].igst += taxCalc.igst;
        summary.by_state[stateKey].total_tax += taxCalc.total_tax;

        // Group by tax rate
        const rateKey = `${service.tax_rate || 0}%`;
        if (!summary.by_rate[rateKey]) {
          summary.by_rate[rateKey] = {
            rate: service.tax_rate || 0,
            total_sales: 0,
            cgst: 0,
            sgst: 0,
            igst: 0,
            total_tax: 0
          };
        }
        
        summary.by_rate[rateKey].total_sales += service.amount;
        summary.by_rate[rateKey].cgst += taxCalc.cgst;
        summary.by_rate[rateKey].sgst += taxCalc.sgst;
        summary.by_rate[rateKey].igst += taxCalc.igst;
        summary.by_rate[rateKey].total_tax += taxCalc.total_tax;
      });
    });

    // Round all values to 2 decimal places
    Object.keys(summary.totals).forEach(key => {
      if (typeof summary.totals[key] === 'number') {
        summary.totals[key] = Math.round(summary.totals[key] * 100) / 100;
      }
    });

    return summary;
  }

  async getInvoicesForPeriod(startDate, endDate) {
    // This would fetch from your invoice service
    try {
      if (this.isOnline) {
        const { data, error } = await supabase
          .from('invoices_2024')
          .select(`
            *,
            client:clients_billing_2024(*)
          `)
          .gte('issue_date', startDate)
          .lte('issue_date', endDate)
          .order('issue_date', { ascending: true });
        
        return { data: data || [], error };
      } else {
        // Demo data for offline mode
        const demoInvoices = [
          {
            id: 1,
            invoice_number: 'INV-2024-001',
            issue_date: '2024-01-15',
            client: {
              name: 'TechCorp Ltd',
              business_name: 'TechCorp Ltd',
              gstin: '27AABCT1332L000',
              gst_type: 'B2B',
              state: 'Maharashtra'
            },
            services: [
              { name: 'SEO Services', amount: 10000, tax_rate: 18 },
              { name: 'PPC Management', amount: 5000, tax_rate: 18 }
            ],
            total_amount: 17700
          },
          {
            id: 2,
            invoice_number: 'INV-2024-002',
            issue_date: '2024-01-20',
            client: {
              name: 'StartupXYZ',
              business_name: 'StartupXYZ Pvt Ltd',
              gstin: '29AABCS5555N000',
              gst_type: 'B2B',
              state: 'Karnataka'
            },
            services: [
              { name: 'Web Development', amount: 25000, tax_rate: 18 }
            ],
            total_amount: 29500
          },
          {
            id: 3,
            invoice_number: 'INV-2024-003',
            issue_date: '2024-01-25',
            client: {
              name: 'Individual Client',
              business_name: 'John Doe',
              gstin: '',
              gst_type: 'B2C',
              state: 'Maharashtra'
            },
            services: [
              { name: 'Consultation', amount: 5000, tax_rate: 0 }
            ],
            total_amount: 5000
          }
        ];
        
        const filtered = demoInvoices.filter(inv => 
          inv.issue_date >= startDate && inv.issue_date <= endDate
        );
        
        return { data: filtered, error: null };
      }
    } catch (error) {
      return { data: [], error: error.message };
    }
  }

  async generateGSTR1(month, year) {
    const startDate = format(startOfMonth(new Date(year, month - 1)), 'yyyy-MM-dd');
    const endDate = format(endOfMonth(new Date(year, month - 1)), 'yyyy-MM-dd');
    
    const taxSummary = await this.getTaxSummary(startDate, endDate);
    
    const gstr1 = {
      return_period: `${month.toString().padStart(2, '0')}${year}`,
      gstin: companyInfo.gstin,
      legal_name: companyInfo.name,
      filing_date: format(new Date(), 'dd-MM-yyyy'),
      
      // Table 3.1 - B2B Supplies
      b2b_supplies: [],
      
      // Table 3.2 - B2C Supplies  
      b2c_supplies: [],
      
      // Summary
      summary: {
        total_taxable_value: taxSummary.totals.total_sales,
        total_igst: taxSummary.totals.total_igst,
        total_cgst: taxSummary.totals.total_cgst,
        total_sgst: taxSummary.totals.total_sgst,
        total_tax: taxSummary.totals.total_tax
      }
    };

    // Process invoices for GSTR-1 format
    taxSummary.invoices.forEach(invoice => {
      const isB2B = invoice.client?.gst_type === 'B2B';
      
      if (isB2B) {
        // B2B Supply entry
        const clientState = this.extractStateFromGSTIN(invoice.client?.gstin);
        const supplierState = this.getStateByCode(companyInfo.state_code);
        
        invoice.services?.forEach(service => {
          const taxCalc = this.calculateGST(
            service.amount,
            service.tax_rate || 0,
            supplierState?.code,
            clientState?.code,
            true
          );
          
          gstr1.b2b_supplies.push({
            gstin: invoice.client.gstin,
            legal_name: invoice.client.business_name,
            invoice_number: invoice.invoice_number,
            invoice_date: invoice.issue_date,
            taxable_value: service.amount,
            tax_rate: service.tax_rate || 0,
            igst: taxCalc.igst,
            cgst: taxCalc.cgst,
            sgst: taxCalc.sgst,
            place_of_supply: clientState?.code || 'XX'
          });
        });
      } else {
        // B2C Supply entry
        invoice.services?.forEach(service => {
          gstr1.b2c_supplies.push({
            invoice_number: invoice.invoice_number,
            invoice_date: invoice.issue_date,
            taxable_value: service.amount,
            tax_rate: service.tax_rate || 0,
            igst: 0,
            cgst: 0,
            sgst: 0,
            place_of_supply: companyInfo.state_code
          });
        });
      }
    });

    return gstr1;
  }

  async generateGSTR3B(quarter, year) {
    const startDate = format(startOfQuarter(new Date(year, (quarter - 1) * 3)), 'yyyy-MM-dd');
    const endDate = format(endOfQuarter(new Date(year, (quarter - 1) * 3)), 'yyyy-MM-dd');
    
    const taxSummary = await this.getTaxSummary(startDate, endDate);
    
    const gstr3b = {
      return_period: `${quarter}Q${year}`,
      gstin: companyInfo.gstin,
      legal_name: companyInfo.name,
      filing_date: format(new Date(), 'dd-MM-yyyy'),
      
      // Table 3.1 - Outward supplies and inward supplies liable to reverse charge
      outward_supplies: {
        taxable_value: taxSummary.totals.total_sales,
        igst: taxSummary.totals.total_igst,
        cgst: taxSummary.totals.total_cgst,
        sgst: taxSummary.totals.total_sgst,
        cess: 0
      },
      
      // Table 3.2 - Of the supplies shown in 3.1(a) above, details of inter-State supplies made to unregistered persons
      inter_state_unregistered: {
        taxable_value: 0,
        igst: 0
      },
      
      // Table 4 - Eligible ITC
      eligible_itc: {
        igst: 0,
        cgst: 0,
        sgst: 0,
        cess: 0
      },
      
      // Table 5 - Values of exempt, nil-rated and non-GST supplies
      exempt_supplies: {
        inter_state: taxSummary.totals.b2c_sales,
        intra_state: 0
      },
      
      // Summary and liability
      net_liability: {
        igst: taxSummary.totals.total_igst,
        cgst: taxSummary.totals.total_cgst,
        sgst: taxSummary.totals.total_sgst,
        total: taxSummary.totals.total_tax
      }
    };

    return gstr3b;
  }

  async generateGSTR9(year) {
    const startDate = format(startOfYear(new Date(year, 0)), 'yyyy-MM-dd');
    const endDate = format(endOfYear(new Date(year, 0)), 'yyyy-MM-dd');
    
    const taxSummary = await this.getTaxSummary(startDate, endDate);
    
    const gstr9 = {
      financial_year: `${year}-${year + 1}`,
      gstin: companyInfo.gstin,
      legal_name: companyInfo.name,
      filing_date: format(new Date(), 'dd-MM-yyyy'),
      
      // Outward supplies
      outward_supplies: {
        taxable_value: taxSummary.totals.total_sales,
        igst: taxSummary.totals.total_igst,
        cgst: taxSummary.totals.total_cgst,
        sgst: taxSummary.totals.total_sgst,
        cess: 0
      },
      
      // Summary by quarters
      quarterly_summary: [],
      
      // Annual summary
      annual_summary: {
        total_turnover: taxSummary.totals.total_sales + taxSummary.totals.total_tax,
        total_tax_liability: taxSummary.totals.total_tax,
        total_tax_paid: 0, // This would come from payment records
        refund_claimed: 0
      }
    };

    // Generate quarterly breakdown
    for (let q = 1; q <= 4; q++) {
      const qStartDate = format(startOfQuarter(new Date(year, (q - 1) * 3)), 'yyyy-MM-dd');
      const qEndDate = format(endOfQuarter(new Date(year, (q - 1) * 3)), 'yyyy-MM-dd');
      const qSummary = await this.getTaxSummary(qStartDate, qEndDate);
      
      gstr9.quarterly_summary.push({
        quarter: q,
        taxable_value: qSummary.totals.total_sales,
        igst: qSummary.totals.total_igst,
        cgst: qSummary.totals.total_cgst,
        sgst: qSummary.totals.total_sgst
      });
    }

    return gstr9;
  }

  getCompanyInfo() {
    return companyInfo;
  }

  getIndianStates() {
    return indianStates;
  }

  validateGSTIN(gstin) {
    const gstinRegex = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;
    return gstinRegex.test(gstin);
  }

  async getTaxLiabilityReport(startDate, endDate) {
    const summary = await this.getTaxSummary(startDate, endDate);
    
    return {
      period: summary.period,
      liability: {
        igst_liability: summary.totals.total_igst,
        cgst_liability: summary.totals.total_cgst,
        sgst_liability: summary.totals.total_sgst,
        total_liability: summary.totals.total_tax
      },
      payment_due_date: this.calculateDueDate(endDate),
      interest_calculation: this.calculateInterest(summary.totals.total_tax, endDate)
    };
  }

  calculateDueDate(periodEndDate) {
    // GST payment due date is 20th of following month
    const date = new Date(periodEndDate);
    const dueDate = new Date(date.getFullYear(), date.getMonth() + 1, 20);
    return format(dueDate, 'yyyy-MM-dd');
  }

  calculateInterest(taxAmount, fromDate, toDate = new Date()) {
    // 18% per annum interest on delayed payments
    const days = Math.max(0, Math.ceil((new Date(toDate) - new Date(fromDate)) / (1000 * 60 * 60 * 24)));
    const interestRate = 0.18; // 18% per annum
    const interest = (taxAmount * interestRate * days) / 365;
    return Math.round(interest * 100) / 100;
  }
}

export default new TaxService();